import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import StatCard from '../components/StatCard';
import { 
  Building2, 
  Users, 
  Mail, 
  Download, 
  RefreshCw,
  FileDown,
  Search,
  LogOut
} from 'lucide-react';
import { 
  getCollegeLeads, 
  getMentorApplications, 
  getContactInquiries,
  getDownloadStats,
  exportToCSV 
} from '../services/adminApi';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Admin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [collegeLeads, setCollegeLeads] = useState([]);
  const [mentorApps, setMentorApps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [downloadStats, setDownloadStats] = useState({});
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    verifyAuth();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Invalid session');
      }

      setVerifying(false);
      loadData();
    } catch (error) {
      console.error('Auth verification failed:', error);
      localStorage.removeItem('admin_token');
      toast.error('Session expired. Please login again.');
      navigate('/admin/login');
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');
    
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [leads, mentors, contactsData, downloads] = await Promise.all([
        getCollegeLeads(),
        getMentorApplications(),
        getContactInquiries(),
        getDownloadStats()
      ]);
      
      setCollegeLeads(leads);
      setMentorApps(mentors);
      setContacts(contactsData);
      setDownloadStats(downloads);
      toast.success('Data loaded successfully');
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (data, filename) => {
    exportToCSV(data, filename);
    toast.success(`Exported ${filename}`);
  };

  const filterData = (data, searchFields) => {
    let filtered = data;

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => item.status === filterStatus);
    }

    // Search
    if (searchTerm) {
      filtered = filtered.filter(item =>
        searchFields.some(field =>
          String(item[field] || '').toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    return filtered;
  };

  const getStatusColor = (status) => {
    const colors = {
      new: '#33c375',
      pending: '#f59e0b',
      contacted: '#3b82f6',
      reviewing: '#8b5cf6',
      scheduled: '#10b981',
      approved: '#059669',
      completed: '#6b7280',
      resolved: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };


  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#fffaf1'}}>
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4" size={40} style={{color: '#33c375'}} />
          <p className="text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#fffaf1'}}>
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4" size={48} style={{color: '#33c375'}} />
          <p className="text-lg" style={{color: '#012726'}}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const filteredLeads = filterData(collegeLeads, ['college_name', 'contact_person', 'email']);
  const filteredMentors = filterData(mentorApps, ['full_name', 'email', 'current_company']);
  const filteredContacts = filterData(contacts, ['name', 'email', 'subject']);

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#fffaf1'}}>
      <div className="container mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold" style={{color: '#012726'}}>Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">Manage all submissions and analytics</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={loadData}
              className="text-white"
              style={{backgroundColor: '#33c375'}}
            >
              <RefreshCw className="mr-2" size={16} />
              Refresh
            </Button>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2"
              style={{borderColor: '#dc2626', color: '#dc2626'}}
            >
              <LogOut size={16} />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="College Leads"
            value={collegeLeads.length}
            icon={Building2}
            description={`${collegeLeads.filter(l => l.status === 'new').length} new`}
            color="#012726"
          />
          <StatCard
            title="Mentor Applications"
            value={mentorApps.length}
            icon={Users}
            description={`${mentorApps.filter(m => m.status === 'pending').length} pending`}
            color="#33c375"
          />
          <StatCard
            title="Contact Inquiries"
            value={contacts.length}
            icon={Mail}
            description={`${contacts.filter(c => c.status === 'new').length} new`}
            color="#3b82f6"
          />
          <StatCard
            title="Total Downloads"
            value={downloadStats.total_downloads || 0}
            icon={Download}
            description="Brochures & Guides"
            color="#f59e0b"
          />
        </div>

        {/* Search and Filter */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  placeholder="Search by name, email, company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={filterStatus === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('all')}
                  style={filterStatus === 'all' ? {backgroundColor: '#33c375', color: 'white'} : {}}
                >
                  All
                </Button>
                <Button
                  variant={filterStatus === 'new' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('new')}
                  style={filterStatus === 'new' ? {backgroundColor: '#33c375', color: 'white'} : {}}
                >
                  New
                </Button>
                <Button
                  variant={filterStatus === 'pending' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('pending')}
                  style={filterStatus === 'pending' ? {backgroundColor: '#33c375', color: 'white'} : {}}
                >
                  Pending
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Tables */}
        <Tabs defaultValue="colleges" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
            <TabsTrigger value="colleges">College Leads ({filteredLeads.length})</TabsTrigger>
            <TabsTrigger value="mentors">Mentors ({filteredMentors.length})</TabsTrigger>
            <TabsTrigger value="contacts">Contacts ({filteredContacts.length})</TabsTrigger>
          </TabsList>

          {/* College Leads Table */}
          <TabsContent value="colleges">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>College Demo Requests</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport(filteredLeads, 'college-leads.csv')}
                >
                  <FileDown className="mr-2" size={16} />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredLeads.map((lead) => (
                    <div key={lead.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold" style={{color: '#012726'}}>
                            {lead.college_name}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            <strong>Contact:</strong> {lead.contact_person} 
                            {lead.designation && ` (${lead.designation})`}
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Email:</strong> {lead.email} | <strong>Phone:</strong> {lead.phone}
                          </p>
                          {lead.message && (
                            <p className="text-sm text-gray-600 mt-2">
                              <strong>Message:</strong> {lead.message}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-2">
                            {new Date(lead.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Badge style={{backgroundColor: getStatusColor(lead.status), color: 'white'}}>
                          {lead.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {filteredLeads.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No college leads found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mentor Applications Table */}
          <TabsContent value="mentors">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Mentor Applications</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport(filteredMentors, 'mentor-applications.csv')}
                >
                  <FileDown className="mr-2" size={16} />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredMentors.map((mentor) => (
                    <div key={mentor.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold" style={{color: '#012726'}}>
                            {mentor.name}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            <strong>{mentor.designation}</strong>
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Industry:</strong> {mentor.industry} | <strong>Experience:</strong> {mentor.years_of_experience} years
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>City:</strong> {mentor.city}
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Email:</strong> {mentor.company_email} | <strong>Mobile:</strong> {mentor.mobile_number}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            {new Date(mentor.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Badge style={{backgroundColor: getStatusColor(mentor.status), color: 'white'}}>
                          {mentor.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {filteredMentors.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No mentor applications found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Inquiries Table */}
          <TabsContent value="contacts">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Contact Inquiries</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport(filteredContacts, 'contact-inquiries.csv')}
                >
                  <FileDown className="mr-2" size={16} />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredContacts.map((contact) => (
                    <div key={contact.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold" style={{color: '#012726'}}>
                            {contact.name}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            <strong>Subject:</strong> {contact.subject}
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Email:</strong> {contact.email}
                            {contact.phone && ` | Phone: ${contact.phone}`}
                          </p>
                          <p className="text-sm text-gray-600 mt-2">
                            <strong>Message:</strong> {contact.message}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            Type: {contact.inquiry_type} | {new Date(contact.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Badge style={{backgroundColor: getStatusColor(contact.status), color: 'white'}}>
                          {contact.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {filteredContacts.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No contact inquiries found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Download Stats */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Download Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Total Downloads</p>
                <p className="text-3xl font-bold mt-2" style={{color: '#012726'}}>
                  {downloadStats.total_downloads || 0}
                </p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">College Brochure</p>
                <p className="text-3xl font-bold mt-2" style={{color: '#33c375'}}>
                  {downloadStats.by_document_type?.brochure || 0}
                </p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Mentor Guide</p>
                <p className="text-3xl font-bold mt-2" style={{color: '#3b82f6'}}>
                  {downloadStats.by_document_type?.mentor_guide || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Admin;
